import React from 'react';
import type { ErrorInfo } from 'react';
import { Button, Space } from 'antd';
import {
  CloudServerOutlined,
  FileSearchOutlined,
  HomeOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../../store/useAuthStore';
import { getDefaultErrorHomePath } from '../../../utils/errorNavigation';
import AppErrorLayout, { type AppErrorLayoutMode } from './AppErrorLayout';
import type { AppErrorVariant } from './types';
import { APP_ERROR_VARIANT_DEFAULTS } from './variantContent';

import './app-error.css';

const VARIANT_ICONS: Record<AppErrorVariant, React.ReactNode> = {
  notFound: <FileSearchOutlined className="app-error-card__icon" aria-hidden />,
  server: <CloudServerOutlined className="app-error-card__icon" aria-hidden />,
  unexpected: <WarningOutlined className="app-error-card__icon" aria-hidden />,
};

export interface AppErrorPageProps {
  variant: AppErrorVariant;
  title?: string;
  description?: string;
  supportCode?: string;
  layout?: AppErrorLayoutMode;
  onRetry?: () => void;
  showHomeButton?: boolean;
  homePath?: string;
  devError?: Error;
  devErrorInfo?: ErrorInfo;
}

const AppErrorPage: React.FC<AppErrorPageProps> = ({
  variant,
  title,
  description,
  supportCode,
  layout = 'fullPage',
  onRetry,
  showHomeButton = true,
  homePath,
  devError,
  devErrorInfo,
}) => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const defaults = APP_ERROR_VARIANT_DEFAULTS[variant];
  const resolvedTitle = title ?? defaults.title;
  const resolvedDescription = description ?? defaults.description;
  const resolvedHomePath = homePath ?? getDefaultErrorHomePath(user);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate(resolvedHomePath);
  };

  const cardClass =
    layout === 'embedded' ? 'app-error-card app-error-card--embedded' : 'app-error-card';

  const content = (
    <article className={cardClass} role="alert">
      <div className="app-error-card__icon-wrap">{VARIANT_ICONS[variant]}</div>
      <h1 className="app-error-card__title">{resolvedTitle}</h1>
      <p className="app-error-card__description">{resolvedDescription}</p>
      <div className="app-error-card__actions">
        <Space wrap>
          {(variant === 'server' || variant === 'unexpected') && (
            <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry}>
              Tentar novamente
            </Button>
          )}
          {showHomeButton && (
            <Button
              type={variant === 'notFound' ? 'primary' : 'default'}
              icon={<HomeOutlined />}
              onClick={handleGoHome}
            >
              {variant === 'notFound' ? 'Ir para o início' : 'Voltar ao início'}
            </Button>
          )}
          {variant === 'notFound' && user ? (
            <Button type="default" onClick={() => navigate('/myAccount')}>
              Minha conta
            </Button>
          ) : null}
        </Space>
      </div>
      {supportCode ? (
        <p className="app-error-card__support" aria-label="Código de suporte">
          Código: {supportCode}
          {variant === 'server' ? ' · Erro 500' : null}
        </p>
      ) : null}
      {process.env.NODE_ENV === 'development' && devError ? (
        <details className="app-error-card__dev-details">
          <summary>Detalhes do erro (desenvolvimento)</summary>
          <pre className="app-error-card__dev-pre">
            {devError.toString()}
            {devErrorInfo?.componentStack}
          </pre>
        </details>
      ) : null}
    </article>
  );

  if (layout === 'embedded') {
    return content;
  }

  return <AppErrorLayout mode={layout}>{content}</AppErrorLayout>;
};

export default AppErrorPage;
